import EverTrue from 'app';
import BaseRouter from 'base/base-router';
import CadencesV2Controller from './controllers/cadences-v2-controller';
import CreateCadencePage from './components/create-cadence/create-cadence-page';
import CadenceDetailPage from './components/detail/cadence-detail-page';
import CadenceDebugPage from './components/detail/cadence-debug-page';
import TemplateDetailPage from './components/detail/template-detail-page';
import CreateBulkMessagePage from './components/bulk-message/create-bulk-message-page';
import BulkMessageDetailPage from './components/bulk-message/bulk-message-detail-page';
import IsFlaggedController from 'components/flags/is-flagged-controller';
import { EmailTemplatesProvider } from 'apps/outreach/email/templates/email-templates-context';
import { EmailSignaturesProvider } from 'apps/outreach/email/signatures/email-signatures-context';

/**
 * Router for Cadences 2.0 pages.
 */
class CadencesV2Router extends BaseRouter {
  static initClass() {
    this.prototype.routes = {
      'cadences-v2(/)': 'index',
      'cadences-v2/create(/)': 'create',
      'cadences-v2/create/:type': 'createWithType',
      'cadences-v2/bulk-message/create(/)': 'createBulkMessage',
      'cadences-v2/bulk-message/:id/edit': 'editBulkMessage',
      'cadences-v2/bulk-message/:id': 'bulkMessageDetail',
      'cadences-v2/template/:id': 'templateDetail',
      'cadences-v2/:id/debug': 'debugCadence',
      'cadences-v2/:id/edit': 'editCadence',
      'cadences-v2/:id': 'detail',
    };
  }

  // Cadences 2.0 Index Page
  async index() {
    EverTrue.track.set('cadences_v2', { type: 'left_nav' });
    EverTrue.execute('setBrowserTitle', 'Cadences 2.0');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <CadencesV2Controller />
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Secret debug/ops console — access via /cadences-v2/:id/debug
  debugCadence(id) {
    EverTrue.track.set('cadences_v2', { type: 'debug', cadence_id: id });
    EverTrue.execute('setBrowserTitle', 'Cadence Debug');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <CadenceDebugPage cadenceId={id} />
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Cadences 2.0 Detail Page
  detail(id) {
    EverTrue.track.set('cadences_v2', { type: 'detail', cadence_id: id });
    EverTrue.execute('setBrowserTitle', 'Cadence Detail');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <CadenceDetailPage cadenceId={id} />
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Create Cadence Page
  create() {
    this.createWithType('cadence');
  }

  // Create Cadence with specific type
  createWithType(type) {
    const params = new URLSearchParams(window.location.search);
    const shellId = params.get('shell') || null;

    if (type === 'bulk-message') {
      EverTrue.track.set('cadences_v2', { type: 'create_bulk_message' });
      EverTrue.execute('setBrowserTitle', 'Create Bulk Message');
      this.currentView = (
        <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
          <EmailTemplatesProvider>
            <EmailSignaturesProvider>
              <CreateBulkMessagePage preSelectedShellId={shellId} />
            </EmailSignaturesProvider>
          </EmailTemplatesProvider>
        </IsFlaggedController>
      );
    } else {
      EverTrue.track.set('cadences_v2', { type: 'create', cadence_type: type });
      EverTrue.execute('setBrowserTitle', 'Create Cadence');
      this.currentView = (
        <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
          <EmailTemplatesProvider>
            <EmailSignaturesProvider>
              <CreateCadencePage cadenceType={type} shellId={shellId} />
            </EmailSignaturesProvider>
          </EmailTemplatesProvider>
        </IsFlaggedController>
      );
    }
    EverTrue.page.mount(this.currentView);
  }

  // Create Bulk Message Page
  createBulkMessage() {
    EverTrue.track.set('cadences_v2', { type: 'create_bulk_message' });
    EverTrue.execute('setBrowserTitle', 'Create Bulk Message');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <EmailTemplatesProvider>
          <EmailSignaturesProvider>
            <CreateBulkMessagePage />
          </EmailSignaturesProvider>
        </EmailTemplatesProvider>
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Bulk Message Detail Page
  bulkMessageDetail(id) {
    EverTrue.track.set('cadences_v2', { type: 'bulk_message_detail', message_id: id });
    EverTrue.execute('setBrowserTitle', 'Bulk Message Detail');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <BulkMessageDetailPage messageId={id} />
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Edit Cadence Page
  editCadence(id) {
    EverTrue.track.set('cadences_v2', { type: 'edit', cadence_id: id });
    EverTrue.execute('setBrowserTitle', 'Edit Cadence');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <EmailTemplatesProvider>
          <EmailSignaturesProvider>
            <CreateCadencePage cadenceId={id} />
          </EmailSignaturesProvider>
        </EmailTemplatesProvider>
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Edit Bulk Message Page
  editBulkMessage(id) {
    EverTrue.track.set('cadences_v2', { type: 'edit_bulk_message', message_id: id });
    EverTrue.execute('setBrowserTitle', 'Edit Bulk Message');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <EmailTemplatesProvider>
          <EmailSignaturesProvider>
            <CreateBulkMessagePage cadenceId={id} />
          </EmailSignaturesProvider>
        </EmailTemplatesProvider>
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }

  // Template Detail Page
  templateDetail(id) {
    EverTrue.track.set('cadences_v2', { type: 'template_detail', template_id: id });
    EverTrue.execute('setBrowserTitle', 'Template Detail');
    this.currentView = (
      <IsFlaggedController flags={['signal', 'cadenceV2Internal']}>
        <TemplateDetailPage templateId={id} />
      </IsFlaggedController>
    );
    EverTrue.page.mount(this.currentView);
  }
}

CadencesV2Router.initClass();
export default CadencesV2Router;
